import { useState } from "react";
import {
  Palette,
  Navigation,
  LayoutGrid,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Plus,
  Trash2,
  Image,
  Mail,
  Phone,
  MapPin,
} from "lucide-react";
import { CatalogContent } from "@/api";

interface ShellEditorProps {
  config: any;
  onChange: (config: any) => void;
  canEdit: boolean;
  catalog: CatalogContent;
}

type ShellSection = "brand" | "nav" | "footer";

export function ShellEditor({
  config,
  onChange,
  canEdit,
  catalog,
}: ShellEditorProps) {
  const [expandedSection, setExpandedSection] = useState<ShellSection | null>(
    "brand",
  );

  const shell = config?.site?.shell || {};

  const updateShell = (path: string, value: any) => {
    const newConfig = { ...config };
    if (!newConfig.site) newConfig.site = {};
    if (!newConfig.site.shell) newConfig.site.shell = {};

    // Handle nested paths like 'brand.title'
    const parts = path.split(".");
    let current = newConfig.site.shell;
    for (let i = 0; i < parts.length - 1; i++) {
      if (!current[parts[i]]) current[parts[i]] = {};
      current = current[parts[i]];
    }
    current[parts[parts.length - 1]] = value;

    onChange(newConfig);
  };

  const sections = [
    {
      id: "brand" as ShellSection,
      label: "Brand",
      icon: Palette,
      description: "Logo, titolo e tema",
    },
    {
      id: "nav" as ShellSection,
      label: "Navigazione",
      icon: Navigation,
      description: "Menu principale e CTA",
    },
    {
      id: "footer" as ShellSection,
      label: "Footer",
      icon: LayoutGrid,
      description: "Contatti, link e social",
    },
  ];

  return (
    <div className="space-y-4">
      {sections.map((section) => {
        const isExpanded = expandedSection === section.id;

        return (
          <div
            key={section.id}
            className="border border-gray-200 rounded-lg overflow-hidden"
          >
            <button
              onClick={() => setExpandedSection(isExpanded ? null : section.id)}
              className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <section.icon className="w-5 h-5 text-gray-600" />
                <div className="text-left">
                  <p className="font-medium text-gray-900">{section.label}</p>
                  <p className="text-xs text-gray-500">{section.description}</p>
                </div>
              </div>
              {isExpanded ? (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronRight className="w-5 h-5 text-gray-400" />
              )}
            </button>

            {isExpanded && (
              <div className="p-4 border-t border-gray-200 bg-white">
                {section.id === "brand" && (
                  <BrandEditor
                    shell={shell}
                    onUpdate={updateShell}
                    canEdit={canEdit}
                    catalog={catalog}
                  />
                )}
                {section.id === "nav" && (
                  <NavEditor
                    shell={shell}
                    onUpdate={updateShell}
                    canEdit={canEdit}
                  />
                )}
                {section.id === "footer" && (
                  <FooterEditor
                    shell={shell}
                    onUpdate={updateShell}
                    canEdit={canEdit}
                    catalog={catalog}
                  />
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Brand Editor
function BrandEditor({
  shell,
  onUpdate,
  canEdit,
  catalog,
}: {
  shell: any;
  onUpdate: (path: string, value: any) => void;
  canEdit: boolean;
  catalog: CatalogContent;
}) {
  const brand = shell?.brand || {};

  const availableThemes =
    catalog?.themes?.map((theme: any) => ({
      id: theme.id,
      label: theme.label,
    })) ?? [];

  return (
    <div className="space-y-4">
      {/* Theme selector */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Tema
        </label>
        <select
          value={shell?.theme || availableThemes[0]}
          onChange={(e) => onUpdate("theme", e.target.value)}
          disabled={!canEdit}
          className="input"
        >
          {availableThemes.map((theme) => (
            <option value={theme.id}>{theme.label}</option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nome sito
          </label>
          <input
            type="text"
            value={brand?.title || ""}
            onChange={(e) => onUpdate("brand.title", e.target.value)}
            placeholder="Es. Museo Civico"
            disabled={!canEdit}
            className="input"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Sottotitolo
          </label>
          <input
            type="text"
            value={brand?.subtitle || ""}
            onChange={(e) => onUpdate("brand.subtitle", e.target.value)}
            placeholder="Es. Patrimonio culturale"
            disabled={!canEdit}
            className="input"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <Image className="w-4 h-4 inline mr-1" />
            Logo principale
          </label>
          <input
            type="text"
            value={brand?.logoUrl || ""}
            onChange={(e) => onUpdate("brand.logoUrl", e.target.value)}
            placeholder="/assets/logo.svg"
            disabled={!canEdit}
            className="input font-mono text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <Image className="w-4 h-4 inline mr-1" />
            Logo small
          </label>
          <input
            type="text"
            value={brand?.logoSmallUrl || ""}
            onChange={(e) => onUpdate("brand.logoSmallUrl", e.target.value)}
            placeholder="/assets/logo-small.svg"
            disabled={!canEdit}
            className="input font-mono text-sm"
          />
        </div>
      </div>

      {/* Preview */}
      {(brand?.title || brand?.subtitle) && (
        <div className="mt-4 p-4 bg-gray-100 rounded-lg">
          <p className="text-xs text-gray-500 mb-2">Anteprima:</p>
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gray-300 rounded flex items-center justify-center text-gray-500 text-xs">
              Logo
            </div>
            <div>
              <p className="font-semibold text-gray-900">
                {brand?.title || "Nome sito"}
              </p>
              {brand?.subtitle && (
                <p className="text-sm text-gray-600">{brand.subtitle}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Navigation Editor
function NavEditor({
  shell,
  onUpdate,
  canEdit,
}: {
  shell: any;
  onUpdate: (path: string, value: any) => void;
  canEdit: boolean;
}) {
  const nav = shell?.nav || {};
  const items = nav?.items || [];
  const ctaButton = nav?.ctaButton;

  const addNavItem = () => {
    const newItems = [...items, { label: "Nuova voce", href: "/" }];
    onUpdate("nav.items", newItems);
  };

  const updateNavItem = (index: number, field: string, value: string) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    onUpdate("nav.items", newItems);
  };

  const removeNavItem = (index: number) => {
    const newItems = items.filter((_: any, i: number) => i !== index);
    onUpdate("nav.items", newItems);
  };

  const moveNavItem = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= items.length) return;
    const updatedItems = [...items];
    const [movedItem] = updatedItems.splice(fromIndex, 1);
    updatedItems.splice(toIndex, 0, movedItem);
    onUpdate("nav.items", updatedItems);
  };

  return (
    <div className="space-y-4">
      {/* Menu items */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Voci menu
          </label>

          {canEdit && (
            <button
              onClick={addNavItem}
              className="text-sm text-primary-600 hover:text-primary-700 flex items-center"
            >
              <Plus className="w-4 h-4 mr-1" />
              Aggiungi
            </button>
          )}
        </div>

        {items.length === 0 ? (
          <p className="text-sm text-gray-500 italic">Nessuna voce nel menu</p>
        ) : (
          <div className="space-y-2">
            {items.map((item: any, index: number) => (
              <div
                key={index}
                className="flex items-center space-x-2 p-2 bg-gray-50 rounded-lg"
              >
                {canEdit && (
                  <div className="flex flex-col">
                    <button
                      type="button"
                      onClick={() => moveNavItem(index, index - 1)}
                      disabled={index === 0}
                      className="p-1 text-gray-500 hover:bg-gray-200 rounded disabled:opacity-30"
                    >
                      <ChevronUp className="w-4 h-4" />
                    </button>

                    <button
                      type="button"
                      onClick={() => moveNavItem(index, index + 1)}
                      disabled={index === items.length - 1}
                      className="p-1 text-gray-500 hover:bg-gray-200 rounded disabled:opacity-30"
                    >
                      <ChevronDown className="w-4 h-4" />
                    </button>
                  </div>
                )}

                <input
                  type="text"
                  value={item.label || ""}
                  onChange={(e) =>
                    updateNavItem(index, "label", e.target.value)
                  }
                  placeholder="Label"
                  disabled={!canEdit}
                  className="input flex-1 text-sm"
                />

                <input
                  type="text"
                  value={item.href || ""}
                  onChange={(e) => updateNavItem(index, "href", e.target.value)}
                  placeholder="/path"
                  disabled={!canEdit}
                  className="input flex-1 text-sm font-mono"
                />

                {canEdit && (
                  <button
                    onClick={() => removeNavItem(index)}
                    className="p-1 text-red-500 hover:bg-red-50 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      {/* CTA Button */}
      <div className="border-t border-gray-200 pt-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Pulsante CTA (opzionale)
        </label>
        <div className="grid grid-cols-2 gap-2">
          <input
            type="text"
            value={ctaButton?.label || ""}
            onChange={(e) =>
              onUpdate("nav.ctaButton", { ...ctaButton, label: e.target.value })
            }
            placeholder="Es. Biglietti"
            disabled={!canEdit}
            className="input text-sm"
          />
          <input
            type="text"
            value={ctaButton?.href || ""}
            onChange={(e) =>
              onUpdate("nav.ctaButton", { ...ctaButton, href: e.target.value })
            }
            placeholder="/visita"
            disabled={!canEdit}
            className="input text-sm font-mono"
          />
        </div>
      </div>
    </div>
  );
}

// Footer Editor
function FooterEditor({
  shell,
  onUpdate,
  canEdit,
  catalog,
}: {
  shell: any;
  onUpdate: (path: string, value: any) => void;
  canEdit: boolean;
  catalog: CatalogContent;
}) {
  const footer = shell?.footer || {};
  const contact = footer?.contact || {};
  const social = footer?.social || [];
  const bottomLinks = footer?.bottomLinks || [];
  const columns = footer?.columns || [];
  const poweredBy = footer?.poweredBy || {};

  type PoweredByImage = {
    name: string;
    imageUrl: string;
    linkUrl?: string;
  };

  const images: PoweredByImage[] = footer?.poweredBy?.images || [];

  type SocialLinksBinding = {
    label: string;
    description: string;
    platforms: string[];
  };

  const socialLinks = catalog?.bindingTypes?.socialLinks as SocialLinksBinding;

  const allPlatforms: string[] = socialLinks?.platforms ?? [];

  const updateContact = (field: string, value: string) => {
    onUpdate("footer.contact", { ...contact, [field]: value });
  };

  const addSocialLink = () => {
    const newSocial = [
      ...social,
      { platform: "facebook", url: "", variant: "icon" },
    ];
    onUpdate("footer.social", newSocial);
  };

  const updateSocialLink = (index: number, field: string, value: string) => {
    const newSocial = [...social];
    newSocial[index] = { ...newSocial[index], [field]: value };
    onUpdate("footer.social", newSocial);
  };

  const removeSocialLink = (index: number) => {
    const newSocial = social.filter((_: any, i: number) => i !== index);
    onUpdate("footer.social", newSocial);
  };

  const updatePoweredByTitle = (value: string) => {
    onUpdate("footer.poweredBy", {
      ...poweredBy,
      title: value,
    });
  };

  const addPoweredByImage = () => {
    onUpdate("footer.poweredBy", {
      ...poweredBy,
      images: [
        ...(poweredBy?.images || []),
        {
          imageUrl: "",
          name: "",
          linkUrl: "",
        },
      ],
    });
  };

  const updatePoweredByImage = (
    index: number,
    field: string,
    value: string,
  ) => {
    const newImages = [...(poweredBy?.images || [])];

    newImages[index] = {
      ...newImages[index],
      [field]: value,
    };

    onUpdate("footer.poweredBy", {
      ...poweredBy,
      images: newImages,
    });
  };

  const removePoweredByImage = (index: number) => {
    const newImages = (poweredBy?.images || []).filter(
      (_: any, i: number) => i !== index,
    );

    onUpdate("footer.poweredBy", {
      ...poweredBy,
      images: newImages,
    });
  };

  const addBottomLink = () => {
    onUpdate("footer.bottomLinks", [
      ...bottomLinks,
      {
        label: "",
        href: "",
      },
    ]);
  };

  const updateBottomLink = (index: number, field: string, value: string) => {
    const updated = [...bottomLinks];

    updated[index] = {
      ...updated[index],
      [field]: value,
    };

    onUpdate("footer.bottomLinks", updated);
  };

  const removeBottomLink = (index: number) => {
    onUpdate(
      "footer.bottomLinks",
      bottomLinks.filter((_: any, i: number) => i !== index),
    );
  };

  const addColumn = () => {
    onUpdate("footer.columns", [
      ...columns,
      {
        title: "",
        links: [],
      },
    ]);
  };
  const updateColumnTitle = (index: number, title: string) => {
    const updated = [...columns];

    updated[index] = {
      ...updated[index],
      title,
    };

    onUpdate("footer.columns", updated);
  };

  const addColumnLink = (columnIndex: number) => {
    const updated = [...columns];

    updated[columnIndex].links.push({
      label: "",
      href: "",
    });

    onUpdate("footer.columns", updated);
  };

  const updateColumnLink = (
    columnIndex: number,
    linkIndex: number,
    field: string,
    value: string,
  ) => {
    const updated = [...columns];

    updated[columnIndex].links[linkIndex] = {
      ...updated[columnIndex].links[linkIndex],
      [field]: value,
    };

    onUpdate("footer.columns", updated);
  };

  const removeColumn = (index: number) => {
    onUpdate(
      "footer.columns",
      columns.filter((_: any, i: number) => i !== index),
    );
  };

  const removeColumnLink = (columnIndex: number, linkIndex: number) => {
    const updated = [...columns];

    updated[columnIndex].links = updated[columnIndex].links.filter(
      (_: any, i: number) => i !== linkIndex,
    );

    onUpdate("footer.columns", updated);
  };

  const updateShowNewsletter = (value: boolean) => {
    onUpdate("footer.showNewsletter", value);
  };

  return (
    <div className="space-y-6">
      {/* Contact info */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Informazioni di contatto
        </label>
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <MapPin className="w-4 h-4 text-gray-400" />
            <textarea
              value={contact?.address || ""}
              onChange={(e) => updateContact("address", e.target.value)}
              placeholder="Via Example, 1&#10;00100 Roma"
              disabled={!canEdit}
              className="input flex-1 text-sm"
              rows={2}
            />
          </div>
          <div className="flex items-center space-x-2">
            <Phone className="w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={contact?.phone || ""}
              onChange={(e) => updateContact("phone", e.target.value)}
              placeholder="+39 06 1234567"
              disabled={!canEdit}
              className="input flex-1 text-sm"
            />
          </div>
          <div className="flex items-center space-x-2">
            <Mail className="w-4 h-4 text-gray-400" />
            <input
              type="email"
              value={contact?.email || ""}
              onChange={(e) => updateContact("email", e.target.value)}
              placeholder="info@museo.it"
              disabled={!canEdit}
              className="input flex-1 text-sm"
            />
          </div>
        </div>
      </div>

      {/* Footer Columns */}
      <div className="border-t border-gray-200 pt-4">
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Colonne Footer
          </label>

          {canEdit && (
            <button
              onClick={addColumn}
              className="text-sm text-primary-600 hover:text-primary-700 flex items-center"
            >
              <Plus className="w-4 h-4 mr-1" />
              Aggiungi colonna
            </button>
          )}
        </div>

        {columns.length === 0 ? (
          <p className="text-sm text-gray-500 italic">
            Nessuna colonna configurata
          </p>
        ) : (
          <div className="space-y-4">
            {columns.map((column: any, columnIndex: number) => (
              <div
                key={columnIndex}
                className="border rounded-lg p-3 space-y-3"
              >
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={column.title || ""}
                    onChange={(e) =>
                      updateColumnTitle(columnIndex, e.target.value)
                    }
                    placeholder="Titolo colonna"
                    disabled={!canEdit}
                    className="input flex-1 text-sm"
                  />

                  {canEdit && (
                    <button
                      onClick={() => removeColumn(columnIndex)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>

                <div className="space-y-2">
                  {column.links?.map((link: any, linkIndex: number) => (
                    <div key={linkIndex} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={link.label || ""}
                        onChange={(e) =>
                          updateColumnLink(
                            columnIndex,
                            linkIndex,
                            "label",
                            e.target.value,
                          )
                        }
                        placeholder="Label"
                        disabled={!canEdit}
                        className="input flex-1 text-sm"
                      />

                      <input
                        type="text"
                        value={link.href || ""}
                        onChange={(e) =>
                          updateColumnLink(
                            columnIndex,
                            linkIndex,
                            "href",
                            e.target.value,
                          )
                        }
                        placeholder="/pagina"
                        disabled={!canEdit}
                        className="input flex-1 text-sm"
                      />

                      {canEdit && (
                        <button
                          onClick={() =>
                            removeColumnLink(columnIndex, linkIndex)
                          }
                          className="p-1 text-red-500 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                {canEdit && (
                  <button
                    onClick={() => addColumnLink(columnIndex)}
                    className="text-sm text-primary-600 hover:text-primary-700 flex items-center"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Aggiungi link
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Social links */}
      <div className="border-t border-gray-200 pt-4">
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Link social
          </label>
          {canEdit && (
            <button
              onClick={addSocialLink}
              className="text-sm text-primary-600 hover:text-primary-700 flex items-center"
            >
              <Plus className="w-4 h-4 mr-1" />
              Aggiungi
            </button>
          )}
        </div>

        {social.length === 0 ? (
          <p className="text-sm text-gray-500 italic">Nessun link social</p>
        ) : (
          <div className="space-y-2">
            {social.map((item: any, index: number) => (
              <div key={index} className="flex items-center space-x-2">
                <div className="space-y-1.5">
                  <select
                    value={item.platform || "facebook"}
                    onChange={(e) =>
                      updateSocialLink(index, "platform", e.target.value)
                    }
                    disabled={!canEdit}
                    className="input w-32 text-sm"
                  >
                    {allPlatforms.map((platform) => (
                      <option key={platform} value={platform}>
                        {platform.charAt(0).toUpperCase() + platform.slice(1)}
                      </option>
                    ))}
                  </select>
                  <select
                    value={item.variant || "icon"}
                    onChange={(e) =>
                      updateSocialLink(index, "variant", e.target.value)
                    }
                    disabled={!canEdit}
                    className="input w-32 text-sm"
                  >
                    <option value="icon">Solo icona</option>
                    <option value="text">Solo testo</option>
                    <option value="both">Icona + testo</option>
                  </select>
                </div>
                <input
                  type="url"
                  value={item.url || ""}
                  onChange={(e) =>
                    updateSocialLink(index, "url", e.target.value)
                  }
                  placeholder="https://..."
                  disabled={!canEdit}
                  className="input flex-1 text-sm font-mono"
                />
                {canEdit && (
                  <button
                    onClick={() => removeSocialLink(index)}
                    className="p-1 text-red-500 hover:bg-red-50 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Powered By  */}

      <div className="border-t border-gray-200 pt-4">
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Prodotti tecnologici utilizzati dal sito
          </label>

          {canEdit && (
            <button
              onClick={addPoweredByImage}
              className="text-sm text-primary-600 hover:text-primary-700 flex items-center"
            >
              <Plus className="w-4 h-4 mr-1" />
              Aggiungi logo
            </button>
          )}
        </div>

        {/* Titolo */}

        <input
          type="text"
          value={poweredBy?.title || ""}
          onChange={(e) => updatePoweredByTitle(e.target.value)}
          placeholder="Titolo: es. Powered by"
          disabled={!canEdit}
          className="input mb-4"
        />

        {images.length === 0 ? (
          <p className="text-sm text-gray-500 italic">
            Nessun logo configurato
          </p>
        ) : (
          <div className="space-y-3">
            {images.map((image, imageIndex) => (
              <div key={imageIndex} className="border rounded-lg p-3 space-y-3">
                <div className="flex flex-col items-center gap-2">
                  <input
                    type="text"
                    value={image.imageUrl || ""}
                    onChange={(e) =>
                      updatePoweredByImage(
                        imageIndex,
                        "imageUrl",
                        e.target.value,
                      )
                    }
                    placeholder="URL immagine: es. /uploads/archives-logo.png"
                    disabled={!canEdit}
                    className="input flex-1"
                  />

                  <input
                    type="text"
                    value={image.name || ""}
                    onChange={(e) =>
                      updatePoweredByImage(imageIndex, "name", e.target.value)
                    }
                    placeholder="Nome prodotto: es. ArcHives"
                    disabled={!canEdit}
                    className="input flex-1"
                  />

                  <input
                    type="text"
                    value={image.linkUrl || ""}
                    onChange={(e) =>
                      updatePoweredByImage(
                        imageIndex,
                        "linkUrl",
                        e.target.value,
                      )
                    }
                    placeholder="Sito web prodotto o azienda: es. https://..."
                    disabled={!canEdit}
                    className="input flex-1"
                  />

                  {canEdit && (
                    <button
                      onClick={() => removePoweredByImage(imageIndex)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Copyright */}
      <div className="border-t border-gray-200 pt-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Copyright
        </label>
        <input
          type="text"
          value={footer?.copyright || ""}
          onChange={(e) => onUpdate("footer.copyright", e.target.value)}
          placeholder="© 2025 Nome Museo. Tutti i diritti riservati."
          disabled={!canEdit}
          className="input text-sm"
        />
      </div>

      {/* Newsletter */}
      <div className="border-t border-gray-200 pt-4">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={footer?.showNewsletter ?? false}
            onChange={(e) => updateShowNewsletter(e.target.checked)}
            disabled={!canEdit}
          />
          <span className="text-sm font-medium">Mostra newsletter</span>
        </label>
      </div>

      {/* Bottom Links (in basso a dx)*/}
      <div className="border-t border-gray-200 pt-4">
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Bottom Links
          </label>

          {canEdit && (
            <button
              onClick={addBottomLink}
              className="text-sm text-primary-600 hover:text-primary-700 flex items-center"
            >
              <Plus className="w-4 h-4 mr-1" />
              Aggiungi
            </button>
          )}
        </div>

        {bottomLinks.length === 0 ? (
          <p className="text-sm text-gray-500 italic">
            Nessun link configurato
          </p>
        ) : (
          <div className="space-y-2">
            {bottomLinks.map((link: any, index: number) => (
              <div key={index} className="flex items-center gap-2">
                <input
                  type="text"
                  value={link.label || ""}
                  onChange={(e) =>
                    updateBottomLink(index, "label", e.target.value)
                  }
                  placeholder="Label"
                  disabled={!canEdit}
                  className="input flex-1 text-sm"
                />

                <input
                  type="text"
                  value={link.href || ""}
                  onChange={(e) =>
                    updateBottomLink(index, "href", e.target.value)
                  }
                  placeholder="/privacy"
                  disabled={!canEdit}
                  className="input flex-1 text-sm"
                />

                {canEdit && (
                  <button
                    onClick={() => removeBottomLink(index)}
                    className="p-1 text-red-500 hover:bg-red-50 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
